import React, { useEffect, useState, useRef } from 'react'
import { useScrollProgress } from '../../../hooks/useParallax'
import { useScrollAnimation } from '../../../hooks/useScrollAnimation'
import styles from './ServicesSection.module.css'

// Animated checkmark that draws itself when visible, then pulses
const AnimatedCheckIcon = ({ isVisible, delay = 0 }) => {
  const [animationPhase, setAnimationPhase] = useState('idle') // idle, drawing, complete

  useEffect(() => {
    if (isVisible) {
      // Start drawing after delay
      const drawTimer = setTimeout(() => setAnimationPhase('drawing'), delay)
      // Complete with pulse after drawing finishes (delay + draw time)
      const completeTimer = setTimeout(() => setAnimationPhase('complete'), delay + 850)
      return () => {
        clearTimeout(drawTimer)
        clearTimeout(completeTimer)
      }
    } else {
      // Reset animation when scrolling away
      setAnimationPhase('idle')
    }
  }, [isVisible, delay])

  const isDrawing = animationPhase === 'drawing' || animationPhase === 'complete'
  const isComplete = animationPhase === 'complete'

  // Path lengths for stroke-dasharray animation
  const circleLength = 63
  const checkLength = 17

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${styles.animatedCheck} ${isComplete ? styles.checkComplete : ''}`}
    >
      {/* Glow filter for pulse effect */}
      <defs>
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      {/* Circle - draws first */}
      <circle
        cx="12"
        cy="12"
        r="10"
        className={styles.checkCircle}
        filter={isComplete ? 'url(#glow)' : 'none'}
        style={{
          strokeDasharray: circleLength,
          strokeDashoffset: isDrawing ? 0 : circleLength,
          transition: isDrawing
            ? 'stroke-dashoffset 0.5s cubic-bezier(0.65, 0, 0.35, 1)'
            : 'none'
        }}
      />
      {/* Checkmark - draws after circle */}
      <polyline
        points="8 12 11 15 16 9"
        className={styles.checkMark}
        filter={isComplete ? 'url(#glow)' : 'none'}
        style={{
          strokeDasharray: checkLength,
          strokeDashoffset: isDrawing ? 0 : checkLength,
          transition: isDrawing
            ? 'stroke-dashoffset 0.3s cubic-bezier(0.65, 0, 0.35, 1) 0.35s'
            : 'none'
        }}
      />
    </svg>
  )
}

const services = [
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12.89 1.45l8 4A2 2 0 0 1 22 7.24v9.53a2 2 0 0 1-1.11 1.79l-8 4a2 2 0 0 1-1.79 0l-8-4a2 2 0 0 1-1.1-1.8V7.24a2 2 0 0 1 1.11-1.79l8-4a2 2 0 0 1 1.78 0z"></path>
        <polyline points="2.32 6.16 12 11 21.68 6.16"></polyline>
        <line x1="12" y1="22.76" x2="12" y2="11"></line>
      </svg>
    ),
    title: 'Architecture Reviews',
    items: [
      'Identify scalability bottlenecks',
      'Security & compliance audit',
      'Cloud cost optimization roadmap'
    ]
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
        <rect x="9" y="9" width="6" height="6"></rect>
        <line x1="9" y1="1" x2="9" y2="4"></line>
        <line x1="15" y1="1" x2="15" y2="4"></line>
        <line x1="9" y1="20" x2="9" y2="23"></line>
        <line x1="15" y1="20" x2="15" y2="23"></line>
        <line x1="20" y1="9" x2="23" y2="9"></line>
        <line x1="20" y1="14" x2="23" y2="14"></line>
        <line x1="1" y1="9" x2="4" y2="9"></line>
        <line x1="1" y1="14" x2="4" y2="14"></line>
      </svg>
    ),
    title: 'AI Integration',
    items: [
      'RAG pipeline design & build',
      'LLM selection & fine-tuning',
      'Intelligent automation workflows'
    ]
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="5" r="3"></circle>
        <line x1="12" y1="8" x2="12" y2="21"></line>
        <path d="M5.5 21a6.5 6.5 0 0 1 13 0"></path>
        <circle cx="5" cy="11" r="2"></circle>
        <circle cx="19" cy="11" r="2"></circle>
      </svg>
    ),
    title: 'Technical Leadership',
    items: [
      'Strategic Technical Advisory',
      'Engineering culture & hiring',
      'Legacy modernization strategy'
    ]
  }
]

const ServicesSection = () => {
  const { ref: sectionRef, isInView } = useScrollProgress()
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({
    threshold: 0.3,
    triggerOnce: false  // Allow animations to replay on every scroll
  })

  return (
    <section id="services" className={styles.services} ref={sectionRef}>
      <div className={styles.container}>
        <div
          ref={headerRef}
          className={styles.header}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <h2 className={styles.sectionTitle}>Services & Engagement</h2>
          <p className={styles.sectionDescription}>
            Collaborate with me to build better systems and stronger teams.
          </p>
          <div className={styles.titleUnderline}></div>
        </div>

        <div className={styles.grid}>
          {services.map((service, index) => (
            <div
              key={index}
              className={styles.card}
              style={{
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateY(0)' : 'translateY(40px)',
                transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.15}s`
              }}
            >
              <div
                className={`${styles.iconWrapper} ${isInView ? styles.iconAnimate : ''}`}
                style={{
                  transitionDelay: `${index * 150 + 200}ms`
                }}
              >
                {service.icon}
              </div>
              <h3 className={styles.title}>{service.title}</h3>
              <ul className={styles.list}>
                {service.items.map((item, idx) => {
                  // Calculate delay: card delay + item stagger
                  const cardDelay = index * 150 // 150ms between cards
                  const itemDelay = idx * 120 // 120ms between items within a card
                  const totalDelay = cardDelay + itemDelay + 400 // +400ms for card entrance

                  return (
                    <li
                      key={idx}
                      className={styles.item}
                      style={{
                        opacity: isInView ? 1 : 0,
                        transform: isInView ? 'translateX(0)' : 'translateX(-10px)',
                        transition: `opacity 0.4s ease ${totalDelay}ms, transform 0.4s ease ${totalDelay}ms`
                      }}
                    >
                      <span className={styles.checkIcon}>
                        <AnimatedCheckIcon
                          isVisible={isInView}
                          delay={totalDelay + 100}
                        />
                      </span>
                      <span>{item}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ServicesSection
